import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Article, VideoCamera } from '@phosphor-icons/react';
import type { LibraryItem } from '@kiipu/app-protocol';
import { platformName } from '../api';
import { isDesktop, privateMediaUrl, usePlatform } from '../lib/client-platform';
import { siteFavicons, sourceHost } from '../lib/site-favicons';

const observed = new Map<Element, (visible: boolean) => void>();
let observer: IntersectionObserver | undefined;
function observe(element: Element, changed: (visible: boolean) => void) {
  if (typeof IntersectionObserver === 'undefined') {
    changed(true);
    return () => undefined;
  }
  observer ??= new IntersectionObserver((entries) => {
    for (const entry of entries) observed.get(entry.target)?.(entry.isIntersecting);
  });
  observed.set(element, changed);
  observer.observe(element);
  return () => {
    observed.delete(element);
    observer?.unobserve(element);
  };
}
/** The same host shares its descriptor, download and decoded URL across all collection views. */
export function SiteIcon({ item }: { item: LibraryItem }) {
  const host = sourceHost(item);
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [image, setImage] = useState<{ key: string; url: string }>();
  const [failed, setFailed] = useState('');
  const [retry, setRetry] = useState(0);
  const online = usePlatform()?.online;
  const descriptor = useSyncExternalStore(
    useCallback((listener) => siteFavicons.subscribe(host, listener), [host]),
    useCallback(() => siteFavicons.get(host), [host]),
    () => undefined,
  );
  const key = `${siteFavicons.identity()}:${host}:${descriptor?.version ?? ''}:${descriptor?.hash ?? ''}:${descriptor?.faviconUrl ?? ''}:${online}:${retry}`;
  useEffect(() => (ref.current ? observe(ref.current, setVisible) : undefined), []);
  useEffect(() => (visible ? siteFavicons.show(host) : undefined), [host, visible]);
  useEffect(() => {
    const retryImage = () => setRetry((value) => value + 1);
    // Descriptor polling is centralized; a ready image only retries when connectivity resumes.
    return siteFavicons.onResume(retryImage);
  }, []);
  useEffect(() => {
    if (!visible || descriptor?.status !== 'ready') return;
    let current = true;
    if (isDesktop()) {
      const url = privateMediaUrl(descriptor.faviconUrl);
      if (url) setImage({ key, url });
    } else
      void siteFavicons.image(descriptor).then((url) => {
        if (current && url) setImage({ key, url });
      });
    return () => {
      current = false;
    };
  }, [descriptor, visible, key]);
  const url = image?.key === key && failed !== key ? image.url : undefined;
  return (
    <span
      ref={ref}
      className={`source-icon source-${item.platform}${url ? ' source-favicon' : ''}`}
      aria-hidden="true"
      data-source-host={host || undefined}
    >
      {url ? (
        <img
          src={url}
          alt=""
          decoding="async"
          onError={() => {
            setFailed(key);
            siteFavicons.contentFailed(host);
          }}
        />
      ) : item.kind === 'video' ? (
        <VideoCamera size={17} />
      ) : item.platform === 'web' ? (
        <Article size={17} />
      ) : (
        platformName(item.platform).slice(0, 1)
      )}
    </span>
  );
}
